import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, map, filter, take } from 'rxjs';
import { AppRoutes } from '../../shared/routes/routes';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private router = inject(Router);
  private auth = inject(AuthService);

  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {

    const currentUrl = this.router.url;

    // Check if we're in 2FA flow - allow access without full authentication
    const is2FARoute = currentUrl.includes('two-factor-verify');

    if (is2FARoute) {
      const twoFactorState = this.auth.getCurrentTwoFactorState();

      if (twoFactorState.required) {
        return true;
      } else {
        const isAuthenticated = this.auth.isAuthenticated();
        const hasUser = !!this.auth.user;

        if (isAuthenticated && hasUser) {
          return true;
        } else {
          return this.router.navigate([AppRoutes.auth.login]).then(() => false);
        }
      }
    }

    // First check if there's a token
    const hasToken = this.auth.isAuthenticated();
    if (!hasToken) {
      return this.router.navigate([AppRoutes.auth.login]).then(() => false);
    }

    // Trigger initialization if not already done
    this.auth.initializeUserSession();

    // Wait for auth state to be resolved
    return this.auth.authState$.pipe(
      filter(state => state !== 'loading'), // Wait until not loading
      take(1), // Take only the first non-loading state
      map(state => {
        if (state === 'authenticated') {
          return true;
        } else {
          this.router.navigate([AppRoutes.auth.login]);
          return false;
        }
      })
    );
  }
}
